// Modules
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios'
import { ToastContainer } from 'react-toastify';

// Components
import { DishComponent } from './components/dish/dish.component';
import { DirectoryComponent } from './components/directory/directory.component';
import { ScheduleComponent } from './components/schedule/schedule.component';
import { LoginComponent } from './components/login/login.component';
import { RegisterComponent } from './components/register/register.component';


// Types
import { UserType } from './types/user.type';

// Styles
import './components/general.scss'
import './components/calendar.scss'
import './App.css';
import { UserSettingsHelper } from './helpers/UserSettingsHelper';
import { UserSettingsType } from './types/user_settings.type';
import { environment, environmentConfig } from './environment';
import { Modal } from './components/modals/modal.component';
import i18next, { t } from 'i18next';
import { ForgotComponent } from './components/forgot/forgot.components';
import { RestoreComponent } from './components/restore/restore.components';
import { ChangePasswordComponent } from './components/changePassword/change-password.components';
import { HelpComponent } from './components/help/help.component';
import { appEventEmitter } from './context/EventContext';
import UserListener from './components/listeners/UserListener';

// Axios Default Config Cors
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
axios.defaults.headers.common['Access-Control-Allow-Methods'] = 'GET,PUT,POST,DELETE,PATCH,OPTIONS';
axios.defaults.headers.common['Access-Control-Allow-Headers'] = 'Origin, X-Requested-With, Content-Type, Accept, Authorization';
axios.defaults.headers.common['x-custom-lang'] = localStorage.getItem('language') || navigator.language.split('-')[0];
axios.defaults.headers.common['Accept-Language'] = localStorage.getItem('language') || navigator.language.split('-')[0];

// When change language update the axios header
i18next.on('languageChanged', (lang) => {
  axios.defaults.headers.common['x-custom-lang'] = lang;
  axios.defaults.headers.common['Accept-Language'] = lang;
});

/**
 * Sippar App
 * @returns {JSX.Element}
 */
function App() {

  // scene selected of the app
  const [scene, setScene] = useState<string | undefined>()

  // user logged
  const [user, setUser] = useState<UserType | undefined>()

  // room setted of videoconference
  const [room, setRoom] = useState<string | undefined>()

  const [userSettings, setUserSettings] = useState<UserSettingsType|undefined>()

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalType, setModalType] = useState<'success' | 'error' | 'warning'>('success');

  const [lastUsedHelp, setLastUsedHelp] = useState<string>('');

  const [mediaIconCache, setMediaIconCache] = useState<Record<string, string>>({});
  const [devicesIconCache, setDevicesIconCache] = useState<Record<string, string>>({});

  const sendFinalEvent = useCallback(() => {
    if (!user) return;

    console.log("[App] Sending final disconnect event via sendBeacon");

    // Enviar el evento de forma síncrona al backend usando sendBeacon (idéntico a UserListener)
    try {
      const apiUrl = `${environment.api.host}/api/usage?app=${encodeURIComponent(environment.usage.app_name)}`;
      const payload = {
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: 'disconnect',
        resource: 'user',
        agent: user.id || 'guest',
        event_phase: 'end',
        session: user.access_token?.split('.')?.[1]?.slice(-10),
        fired_by: user.id,
        fired_at: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(apiUrl, blob);
    } catch (e) {
      console.error('[App] Error sending disconnect event with sendBeacon', e);
    }
  }, [user]);

  useEffect(() => {
    const handleUnload = (event: Event) => {
      console.log("[App] Unload triggered");
      sendFinalEvent();
    };

    // Solo añadir el listener de 'unload'
    window.addEventListener('unload', handleUnload);

    return () => {
      console.log("[App] Component unmounting");
      window.removeEventListener('unload', handleUnload);
    }
  }, [sendFinalEvent]);

  const onClose = () => {
    setIsModalOpen(false)
    setModalButtons(undefined)
    window.location.replace('/')
  }

  const [modalBody, setModalBody] = useState({
    title: '',
    content: ''
  });

  const [modalButtons, setModalButtons] = useState<{
    text: string;
    class: string;
    onClick: () => void;
  }[] | undefined>(undefined);

  const resendConfirmationEmail = async (emailUuid: string) => {
    if (!emailUuid) {
      return;
    }

    try {
      const response = await axios.post(
        `${environment.api.host}/api/mails/resend-confirmation`,
        { email_uuid: emailUuid }
      );

      if (response.data.alreadyConfirmed) {
        setModalType('warning');
        setModalBody({
          title: t('modal.warning.email.title'),
          content: t('login.resend_confirmation.already_confirmed')
        });
        setModalButtons(undefined);
      } else if (response.data.success) {
        setModalType('success');
        setModalBody({
          title: t('modal.success.email.title'),
          content: t('login.resend_confirmation.success')
        });
        setModalButtons(undefined);
      }
    } catch (error) {
      console.error('[App]: Error resending confirmation email', error);
      setModalType('error');
      setModalBody({
        title: t('modal.error.email.title'),
        content: t('login.resend_confirmation.error')
      });
      setModalButtons(undefined);
    }
  };

  const confirmEmail = () => {
    const [controller, action, id] = window.location.pathname.split('/').filter((item) => item)

    if (controller === 'mails' && action === 'confirm' && id) {

      axios.get(`${environment.api.host}/api/mails/confirm/${id}`)
        .then((response) => {
          if (response) {
            setModalType('success')

            if (response && response.data && response.data.errors) {
              setModalType('warning')

              setModalBody({
                title: response.data.errors.email[0] || t('modal.success.email.title'),
                content: response.data.errors.email[1]
              })
            } else {
              setModalBody({
                title: t('modal.success.email.title'),
                content: t('modal.success.email.description_default')
              })

            }
          }

        })
        .catch((error) => {
          console.error(error)

          setModalType('error')

          if (error.response && error.response.data) {
            setModalBody({
              title: t('modal.error.email.title'),
              content: error.response.data.errors.email[0]
            })

            // Agregar botón para reenviar el email de confirmación usando el email_uuid
            setModalButtons([
              {
                text: t('login.resend_confirmation'),
                class: 'resend',
                onClick: () => {
                  // Usar el id (email_uuid) directamente
                  resendConfirmationEmail(id);
                }
              },
              {
                text: t('button.modal.close'),
                class: 'close',
                onClick: onClose
              }
            ]);
          }
        }).finally(() => {
          setIsModalOpen(true)
        })
    }
  }

  const checkProviderLogin = () => {
    const [controller, action] = window.location.pathname.split('/').filter((item) => item)

    if (controller !== 'auth' || action !== 'callback') return

    const params = new URLSearchParams(window.location.search)

    if (!params.has('access_token')) return

    const access_token = params.get('access_token')

    // Get user profile and set user in localstorage
    axios
      .post(`${environment.api.host}/api/auth/my-profile`, null, {
        headers: { Authorization: `Bearer ${access_token}` },
      })
      .then((response) => {
        const userResponse = response.data

        setUser({
          access_token,
          ...userResponse,
        })

        const logedUser = {
          access_token,
          ...userResponse,
        } as UserType

        localStorage.setItem('user', JSON.stringify(logedUser))

        appEventEmitter.emit("login:microsoft", {
          agent: userResponse.id,
          resource: "microsoft",
          user: logedUser,
        });

        window.location.href = '/'
      })
      .catch((error) => {
        setModalType('warning')

        if (error.response && error.response.data) {
          setModalBody({
            title: t('modal.warning.title'),
            content: t('modal.warning.email.description_login')
          })
        }
      }).finally(() => {
        setIsModalOpen(true)

        setTimeout(() => {
          setIsModalOpen(false)
        }, 7000)
      });

  }

  const restorePassword = () => {
    const [controller, action, id] = window.location.pathname.split('/').filter((item) => item)

    if (controller === 'auth' && action === 'forgot' && id) {
      setScene('restore');
    }
  }

  useEffect(() => {

    // Check if user is logged in
    checkProviderLogin()

    // Read window path and check if need to restore password
    restorePassword()

    // Read window path and check if need to confirm email
    confirmEmail()

    try {

      // get user from localstorage
      const user = localStorage.getItem('user')
      if (user) {
        setUser(JSON.parse(user))

        const userObj = JSON.parse(user)
        const langCode = userObj.user_preferred_locale?.code || navigator.language.split('-')[0];

        langCode && i18next.changeLanguage(langCode);
      }

      UserSettingsHelper.getUserSettings(user ? JSON.parse(user) : undefined)
      .then((userSettings) => {
        setUserSettings(userSettings)

        if (userSettings) {
          localStorage.setItem('user_settings', JSON.stringify(userSettings))
        }
      })
      .catch((error) => {
        console.error("[App] Error getting user settings", error);

        const userSettings = localStorage.getItem('user_settings')
        if (userSettings) setUserSettings(JSON.parse(userSettings) as UserSettingsType)
      });

    }
    catch (e) {
      // remove user from localstorage in corrupted JSON
      localStorage.removeItem('user')

    }

  }, [])

  return <div className="App">
    {
      process.env.REACT_APP_ENV === 'preproduction' &&
      <div className='topbar-pre'
            style={
              { backgroundColor: 'orange',
                fontWeight: 'bold',
                width: '100%',
                height: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'fixed',
                top: '0',
                left: '0',
                zIndex: '9999',
                fontSize: '13px',
              }}>
        <p>ENTORNO DE PREPRODUCCIÓN</p>
      </div>
    }
    {
      // @ts-ignore
      window.electron && <div className='topbar'>

        <div className='icon' onClick={() => {

          // @ts-ignore
          window.electron.minimize()

        }}>
          <i className='las la-window-minimize' />
        </div>

        <div className='icon' onClick={() => {
          // @ts-ignore
          window.electron.close()
        }}>
          <i className='las la-times' />
        </div>

      </div>
    }
    {
      user && !user.user_setting.change_password ? <>
        <DishComponent
          room={room}
          setRoom={setRoom}
          setScene={setScene}
          scene={scene}
          userSettings={userSettings}
          setUserSettings={setUserSettings}
          setUser={(user) => {
            if (!user) {
              setUser(undefined)
              localStorage.removeItem('user')
              localStorage.removeItem('user_settings')
              window.location.reload()
              setScene(undefined)
            } else {
              setUser(user)
              localStorage.setItem('user', JSON.stringify(user))
            }
          }}
          user={user}
          setDevicesIconCache={setDevicesIconCache}
          setMediaIconCache={setMediaIconCache}
          devicesIconCache={devicesIconCache}
          mediaIconCache={mediaIconCache}
        />
        {
          scene === 'directory' && <DirectoryComponent
            setScene={setScene}
            userSettings={userSettings}
            user={user}
          />
        }
        {
          scene === 'schedule' && <ScheduleComponent
            setRoom={setRoom}
            userSettings={userSettings}
            setScene={setScene}
            user={user}
          />
        }
        {
          scene === 'help' && <HelpComponent
            setScene={setScene}
            setLastUsedHelp={setLastUsedHelp}
            lastUsedHelp={lastUsedHelp}
          />
        }
      </> : <>

        <img
          src='/images/logo_sippar_h_v2.webp'
          alt='logo'
          className='Logotype sippar-logo'
        />
        {environmentConfig.shouldShowMinDefLogos() && (
          <img
            src='/images/logo_minisdef.svg'
            alt='logo_minisdef'
            className='Logotype minisdef-logo login'
          />
        )}
        {
          scene === 'register' ? <RegisterComponent
            setUser={setUser}
            setScene={setScene}
            onSuccess={(isAutoConfirm: boolean) => {
              setModalType('success')
              setModalBody({
                title: t('modal.register.success.title'),
                content: isAutoConfirm ? t('modal.register.success.auto_confirm') : t('modal.register.success.description')
              })
              setIsModalOpen(true)
            }}
          />
            : scene === 'forgot' ? <ForgotComponent
              setUser={setUser}
              setScene={setScene}
              onSuccess={() => {
                setModalType('success')
                setModalBody({
                  title: t('modal.forgot.success.title'),
                  content: t('modal.forgot.success.description')
                })
                setIsModalOpen(true)
              }}
            />
            : scene === 'restore' ? <RestoreComponent
              setUser={setUser}
              setScene={setScene}
              onSuccess={() => {
                setModalType('success')
                setModalBody({
                  title: t('modal.restore.success.title'),
                  content: t('modal.restore.success.description')
                })
                setIsModalOpen(true)
              }}
            />
            : scene === 'change-password' ? <ChangePasswordComponent
              user={user}
              setScene={setScene}
              onSuccess={() => {
                setModalType('success')
                setModalBody({
                  title: t('modal.change-password.success.title'),
                  content: t('modal.change-password.success.description')
                })
                setIsModalOpen(true)
              }}
            />
            : <LoginComponent
              setUser={setUser}
              setScene={setScene}
              setUserSettings={setUserSettings}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              setModalType={setModalType}
              setModalBody={setModalBody}
            />
        }
      </>}

    <Modal isOpen={isModalOpen} onClose={onClose} type={modalType} body={modalBody} buttons={modalButtons}>

    </Modal>

    <ToastContainer
      position="top-right"
      autoClose={2000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable
      pauseOnHover
      theme="light"
      progressClassName={'toast-progress'}
    />

    {/* Event Listeners */}
    <UserListener />
  </div>

}

export default App;
